/**
 * List of methods with pre-filled args, aka curried functions
 */
import { getIronSession } from 'iron-session';
import {
  GetServerSideProps,
  GetServerSidePropsContext,
  GetServerSidePropsResult,
  NextApiHandler,
  NextComponentType,
  NextPageContext,
} from 'next';
import { BaseContext } from 'next/dist/shared/lib/utils';
import cookie from 'js-cookie';
import PrivateLayout from '../components/layouts/PrivateLayout';
import PublicLayout from '../components/layouts/PublicLayout';
// import { getSession } from '../utility/services/authActions';
import { sessionOptions, transformAuth0IntoQwaSession } from './session';
import { QwaSession, OktaSessionState, AuthenticationStatus } from './types';
import type http from 'http';
import Router from 'next/router';
import { useUser } from '@auth0/nextjs-auth0/client';
import { ComponentType } from 'react';
import { USER_CLASSIFICATION } from '../utility/common';

type GetServerSidePropsWrapper = <P extends { [key: string]: unknown } = { [key: string]: unknown }>(
  getServerSideProps: (
    context: GetServerSidePropsContext
  ) => GetServerSidePropsResult<P> | Promise<GetServerSidePropsResult<P>>
) => GetServerSideProps;

export const withSessionApi = (handler: NextApiHandler): NextApiHandler => {
  return async (req, res) => {
    const session = await getIronSession(req, res, sessionOptions);
    (req as any).session = session;
    return handler(req, res);
  };
};

export const withSessionSsr: GetServerSidePropsWrapper = (getServerSideProps) => {
  return async (context) => {
    const session = await getIronSession(context.req, context.res, sessionOptions);
    (context.req as any).session = session;
    return getServerSideProps(context);
  };
};

/**
 * Prevent rendering or returning info unless use is authenticated
 * */
export const withAuthServerSideProps = (existingGetServerSideProps?: GetServerSideProps): GetServerSideProps => {
  const getServerSideProps: GetServerSideProps = async (context) => {
    // redirect to login page if unauthenticated

    // TODO remember the URL that the user attempted to access and store it here somehow: localStorage, cookie, session, etc
    // then login page will then have to use and clear that data

    if (!(context.req as any).session) {
      return {
        redirect: {
          permanent: false,
          destination: '/login',
        },
      };
    }

    if (existingGetServerSideProps) {
      return existingGetServerSideProps(context);
    }

    return { props: {} };
  };

  return withSessionSsr(getServerSideProps);
};

let featureFlags = {};

export const setFeatureFlags = (flags) => {
  featureFlags = flags;
};

type WrapperProps = {
  featureFlags?: any;
};

// we are using the nextJS types here, so using {} is ok
export const withAuthSync = <C extends BaseContext, IP, P>(
  WrappedComponent: NextComponentType<C, IP, P>
): NextComponentType<C, IP & WrapperProps, P & WrapperProps> => {
  type ExistingPropsType = React.ComponentProps<typeof WrappedComponent>;

  const Wrapper = (props: ExistingPropsType & WrapperProps) => {
    const { user } = useUser();

    // Always stash the current /app/* path on every render. Some pages
    // (e.g. CI3 / content-index.tsx) don't use PrivateLayout, so its
    // path-stashing effect (PrivateLayout.js:211) never runs while the
    // user is there — leaving qwa_last_app_path stale or empty. When
    // the auth dance kicks in (token refresh, missing selected_schema
    // during hydration, SSR redirect through /api/auth/login), user-auth
    // would then fall through to defaultReturnPath = growth-trends.
    // Saving on every render guarantees the latest URL (with query
    // string) is available before any redirect.
    if (typeof window !== 'undefined') {
      const currentPath = `${window.location.pathname}${window.location.search}`;
      // Payment-provider callback URLs must never become a returnTo: replaying
      // one re-triggers purchase verification, which redirects back through
      // /login and lands here again — an unbreakable loop.
      const isPaymentCallback = /[?&](paidPlanPurchase|paidPlanUpdate|daypass)=/.test(window.location.search);
      if (currentPath.startsWith('/app/') && !isPaymentCallback) {
        try {
          sessionStorage.setItem('qwa_last_app_path', currentPath);
        } catch (_) {
          // sessionStorage unavailable (private mode etc.) — ignore;
          // worst case user lands on the default home as before.
        }
      }
    }

    if (user) {
      const qwaSession = transformAuth0IntoQwaSession(user);
      if (!qwaSession.selected_schema && qwaSession.user.userType !== USER_CLASSIFICATION.QWA_TOOLS) {
        // hotfix to redirect users if in case they try to access internal pages
        // AUTH0_TODO lets do this from server side before loading the page
        //
        // Pass the original URL as ?returnTo so user-auth has it directly
        // in router.query — doesn't depend on sessionStorage surviving any
        // of the auth hops. This is the primary mechanism that prevents
        // hard-refresh on /app/content-index?view=monitor&domain=… from
        // bouncing to /app/trends/growth-trends. sessionStorage save above
        // remains as a belt-and-suspenders fallback for the case where
        // an SSR-level redirect (withPageAuthRequired) fires before
        // this client code ever runs.
        // `useUser()` only resolves on the client, so by here `window` is
        // always defined — no SSR branch needed.
        const currentPath = `${window.location.pathname}${window.location.search}`;
        const safePath = currentPath.startsWith('/app/') && !currentPath.startsWith('/user-auth') ? currentPath : '';
        window.location.href = safePath ? `/user-auth?returnTo=${encodeURIComponent(safePath)}` : '/user-auth';
      }
    }
    // we get feature flag only when page is loaded from server, so assigning it variable and using that variables for further pages
    if (props.featureFlags) {
      setFeatureFlags(props.featureFlags);
    }
    // props.user gets users data
    return <WrappedComponent {...props} />;
  };

  Wrapper.Layout = PrivateLayout;
  return Wrapper;
};

export interface WithSessionProps {
  user?: any;
  session?: QwaSession;
}

// to Wrap Class based component with Session
// we cant use useUser with Class components
export const withSession = <P extends object>(
  WrappedComponent: ComponentType<P & WithSessionProps>
): ComponentType<P> => {
  const WithSessionComponent: React.FC<P> = (props) => {
    const { user } = useUser();
    return <WrappedComponent user={user} session={user ? transformAuth0IntoQwaSession(user) : undefined} {...props} />;
  };

  WithSessionComponent.displayName = `withSession(${getDisplayName(WrappedComponent)})`;

  return WithSessionComponent;
};

function getDisplayName<P>(WrappedComponent: ComponentType<P>): string {
  return WrappedComponent.displayName || WrappedComponent.name || 'Component';
}
